import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface CheckboxProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    'checked' | 'onCheckedChange' | 'onChange' | 'children'
  > {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** Tri-state for "select all" headers — wins over `checked` while true. */
  indeterminate?: boolean;
  /** Classes for the label row; `className` styles the box itself. */
  containerClassName?: string;
}

/**
 * DS-30/DS-32 — a 14px box that borrows the field border and focus ring, so a
 * checkbox column in a dense grid lines up with the inputs around it.
 */
export const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  function Checkbox(
    { checked, onChange, label, description, indeterminate = false, disabled, className, containerClassName, id, ...props },
    ref,
  ) {
    const generatedId = React.useId();
    const controlId = id ?? generatedId;
    const descriptionId = `${controlId}-desc`;

    // Undefined stays undefined so the caller can still run this uncontrolled.
    const state = indeterminate ? 'indeterminate' : checked;

    const control = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={controlId}
        disabled={disabled}
        checked={state}
        onCheckedChange={(next) => onChange?.(next === true)}
        aria-describedby={description ? descriptionId : undefined}
        data-testid="checkbox"
        className={cn(
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border border-slate-300 bg-white transition',
          'hover:border-slate-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500',
          'data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:hover:border-blue-700 data-[state=checked]:hover:bg-blue-700',
          'data-[state=indeterminate]:border-blue-600 data-[state=indeterminate]:bg-blue-600',
          'disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:hover:border-slate-200',
          'data-[state=checked]:disabled:border-slate-300 data-[state=checked]:disabled:bg-slate-400',
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white">
          {indeterminate ? <Minus className="h-3 w-3" strokeWidth={3.5} /> : <Check className="h-3 w-3" strokeWidth={3.5} />}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    if (!label && !description) return control;

    return (
      // DS-50 — `gap` and flex order are direction-agnostic, so the label lands
      // on the end side under RTL without a single physical margin.
      <div className={cn('flex items-start gap-2 text-xs', containerClassName)}>
        <span className="flex h-[18px] items-center">{control}</span>
        <span className="min-w-0">
          {label ? (
            <label
              htmlFor={controlId}
              className={cn(
                'block leading-[18px]',
                disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-700',
              )}
            >
              {label}
            </label>
          ) : null}
          {description ? (
            <span id={descriptionId} className="mt-0.5 block text-[11px] leading-tight text-slate-500">
              {description}
            </span>
          ) : null}
        </span>
      </div>
    );
  },
);
